"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { FlaskConical } from "lucide-react";
import type { QuizResponse, RecommendationResult } from "@/lib/quiz-scoring";
import { translatePeptideCategory } from "@/lib/peptide-translations";

interface Props {
	answers: Record<string, string>;
	channel: string;
	locale: string;
	/** Called when a pool item is clicked so parent can scroll/jump. */
	onPick?: (slug: string) => void;
}

const DEBOUNCE_MS = 200;
const POOL_CACHE = new Map<string, RecommendationResult[]>();

function cacheKey(answers: Record<string, string>, channel: string, locale: string) {
	return `${channel}|${locale}|${JSON.stringify(answers)}`;
}

/**
 * Client-side debounced fetch against /api/quiz/recommend.
 * Keeps a per-answer-set cache so back-navigation doesn't refetch.
 * Returns up to 6 results + total pool count; renders a fixed min-height
 * container to keep CLS at 0.
 */
export function QuizLivePool({ answers, channel, locale, onPick }: Props) {
	const t = useTranslations("quiz");
	const answersKey = useMemo(() => cacheKey(answers, channel, locale), [answers, channel, locale]);

	// `cacheVersion` forces a re-read from POOL_CACHE after a fetch mutates it.
	// Pool/total are derived synchronously from the cache on every render — no
	// effect-driven setState to stay clean under react-hooks/set-state-in-effect.
	const [cacheVersion, setCacheVersion] = useState(0);
	const [loading, setLoading] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	const pool = useMemo(
		() => POOL_CACHE.get(answersKey) ?? [],
		// cacheVersion is intentionally in the dep list to re-read after fetch completions.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[answersKey, cacheVersion],
	);
	const total = pool.length;

	useEffect(() => {
		if (POOL_CACHE.has(answersKey)) return;
		const hasAnyAnswer = Object.keys(answers).length > 0;
		if (!hasAnyAnswer) return;

		const handle = window.setTimeout(() => {
			abortRef.current?.abort();
			const ctrl = new AbortController();
			abortRef.current = ctrl;
			setLoading(true);
			fetch("/api/quiz/recommend", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ answers, channel, locale }),
				signal: ctrl.signal,
			})
				.then((r) =>
					r.ok ? (r.json() as Promise<QuizResponse>) : Promise.reject(new Error(`HTTP ${r.status}`)),
				)
				.then((data) => {
					if (ctrl.signal.aborted) return;
					POOL_CACHE.set(answersKey, data.recommendations ?? []);
					setCacheVersion((v) => v + 1);
				})
				.catch((err: unknown) => {
					if (err instanceof DOMException && err.name === "AbortError") return;
					POOL_CACHE.set(answersKey, []);
					setCacheVersion((v) => v + 1);
				})
				.finally(() => setLoading(false));
		}, DEBOUNCE_MS);

		return () => {
			window.clearTimeout(handle);
			abortRef.current?.abort();
		};
		// answersKey already encodes answers/channel/locale; listing them separately would duplicate the signal.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [answersKey]);

	const top = pool.slice(0, 6);
	const rest = Math.max(0, total - top.length);

	return (
		<div
			className="bg-muted/40 flex h-full min-h-[420px] flex-col gap-4 rounded-xl border border-border p-5"
			aria-live="polite"
			aria-busy={loading}
		>
			<div className="flex items-center justify-between">
				<div>
					<p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
						{t("livePool.label")}
					</p>
					<p className="mt-1 flex items-baseline gap-2 text-2xl font-bold tabular-nums">
						<span>{total}</span>
						<span className="text-sm font-medium text-muted-foreground">
							{t("livePool.count", { count: total })}
						</span>
					</p>
				</div>
				<span className="relative flex h-2.5 w-2.5">
					<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
					<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
				</span>
			</div>
			<ul className="flex flex-col gap-2 overflow-auto">
				{top.map((r, i) => (
					<li key={r.peptide.slug}>
						<button
							type="button"
							onClick={() => onPick?.(r.peptide.slug)}
							className="hover:border-primary/40 flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left transition hover:shadow-sm"
						>
							<span
								className={`grid h-6 w-6 shrink-0 place-items-center rounded-md text-[10px] font-bold ${
									i === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-primary"
								}`}
							>
								{i + 1}
							</span>
							<span className="min-w-0 flex-1">
								<span className="flex items-baseline justify-between gap-2">
									<span className="truncate text-sm font-semibold">{r.peptide.name}</span>
									<span className="text-[11px] font-semibold tabular-nums text-primary">
										{r.match.overallPercent}%
									</span>
								</span>
								<span className="mt-1 block truncate text-[11px] text-muted-foreground">
									{r.peptide.categories
										.slice(0, 2)
										.map((c) => translatePeptideCategory(c, locale))
										.join(" · ")}
								</span>
								<span className="mt-1 block h-0.5 rounded bg-muted">
									<span
										className={`block h-full rounded ${i === 0 ? "bg-primary" : "bg-primary/60"}`}
										style={{ width: `${r.match.overallPercent}%` }}
									/>
								</span>
							</span>
						</button>
					</li>
				))}
				{rest > 0 && (
					<li className="pt-1 text-center text-xs text-muted-foreground">
						{t("livePool.moreCount", { count: rest })}
					</li>
				)}
			</ul>
			<p className="mt-auto flex items-start gap-2 border-t border-border pt-3 text-[11px] leading-snug text-muted-foreground">
				<FlaskConical className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
				<span>{t("livePool.updatesHint")}</span>
			</p>
		</div>
	);
}
